import { useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

import { screen } from '@/components/common/breakpointsV2';
import { stringToId } from '@/components/common/utils';
import { useSubNavControllerContext } from '@/components/layout/NavigationBarV2/context/SubNavControllerContext';
import { heroTransition } from '@/lib/animations';

import { SubNavigationColumn } from './SubNavigationColumn';

import type { SubNavigationColumnProps } from './SubNavigationColumn';
import type { Variants } from 'framer-motion';

export interface ColumnDataProps {
  columnOne: SubNavigationColumnProps;
  columnTwo: SubNavigationColumnProps;
  columnThree: SubNavigationColumnProps[];
}
export interface SubNavigationProps {
  isThisSubNavOpen: boolean;
  title: string;
  columns: ColumnDataProps;
  isAnySubNavOpen: boolean;
}

const TRANSITION_DURATION = 0.3;

const variants: Variants = {
  close: {
    height: 0,
    opacity: 0,
    transitionEnd: { visibility: 'hidden' },
  },
  open: { height: 'auto', opacity: 1, visibility: 'visible' },
  morphHeight: ({ lastHeight }: { lastHeight: number }) => ({
    height: 'auto',
    transition: {
      from: lastHeight,
      duration: TRANSITION_DURATION,
      ease: heroTransition.ease,
    },
  }),
  morphVisibility: {
    opacity: 1,
    visibility: 'visible',
    transition: { duration: 0 },
  },
  morphExit: {
    opacity: 0,

    transition: { duration: 0.1 },
    transitionEnd: { visibility: 'hidden' },
  },
};

const containerVariants: Variants = {
  close: { y: -40, opacity: 0 },
  morphExit: { y: -40, opacity: 0 },
  open: { y: 0, opacity: 1 },
  morphVisibility: { y: 0, opacity: 1, transition: { duration: 0 } },
};

export const SubNavigation = ({
  columns,
  isThisSubNavOpen,
  isAnySubNavOpen,
  title,
}: SubNavigationProps) => {
  const ref = useRef<HTMLDivElement>();

  const { columnOne, columnTwo, columnThree } = columns;
  const lastColumn = columnThree?.length > 0 ? columnThree[0] : null;
  const titleString = title ? `-${stringToId(title)}` : null;

  const [lastSubNavHeight, setLastSubNavHeight] = useSubNavControllerContext();

  const isOpeningFromClosed = lastSubNavHeight === null;

  const getVariant = useMemo(() => {
    if (isOpeningFromClosed && isThisSubNavOpen) return 'open';
    if (isThisSubNavOpen) return ['morphHeight', 'morphVisibility'];
    if (isAnySubNavOpen && !isThisSubNavOpen) return 'morphExit';
    return 'close';
  }, [isAnySubNavOpen, isOpeningFromClosed, isThisSubNavOpen]);

  return (
    <Wrapper
      ref={ref}
      key={title}
      id={`sub-navigation${titleString}`}
      transition={{
        duration: TRANSITION_DURATION,
        ease: heroTransition.ease,
      }}
      custom={{ lastHeight: lastSubNavHeight }}
      initial="close"
      animate={getVariant}
      variants={variants}
      onAnimationComplete={(definition) => {
        if (definition === 'open' || definition === 'morphHeight') {
          setLastSubNavHeight(ref.current.getBoundingClientRect().height);
        } else if (definition === 'close') {
          setLastSubNavHeight(null);
        }
      }}
    >
      <Container
        transition={{
          duration: 0.6,
          ease: 'easeOut',
        }}
        variants={containerVariants}
      >
        <SubNavigationColumn {...columnOne} isAnySubNavOpen={isAnySubNavOpen} />
        <SubNavigationColumn {...columnTwo} isAnySubNavOpen={isAnySubNavOpen} />
        <SubNavigationColumn
          {...lastColumn}
          isAnySubNavOpen={isAnySubNavOpen}
        />
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled(motion.div)`
  overflow: hidden;
  background-color: var(--colour-white-dwarf);

  ${screen.md} {
    position: absolute;
    top: calc(100%);
    left: 0;
    right: 0;
    width: 100%;
    background-color: var(--colour-white);
    z-index: 2;
    border-top: 1px solid var(--colour-border-subtle);

    /* This adds a safe zone at the bottom of an open sub-nav
    so that it does not close immediately as the user
    hovers out of the container */
    &::after {
      content: '';
      position: absolute;
      top: calc(100% - 1px);
      left: 0;
      right: 0;
      height: var(--spacing-300);
      background-color: transparent;
      opacity: 0;
    }
  }
`;

const Container = styled(motion.div)`
  width: 100%;
  background-color: var(--colour-white-dwarf);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;

  padding-bottom: var(--spacing-250);

  ${screen.md} {
    padding-bottom: unset;
    grid-template-columns: repeat(3, 1fr);
  }

  ${screen.xl} {
    grid-template-columns: 1fr minmax(300px, 444px) 1fr;
  }
`;
